<template>
    <div class="mx-auto container container--fluid">
        <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
            <span v-if="snackbar_msg != null">{{ snackbar_msg }}</span>
            <v-btn dark text @click="snackbar = false">Close</v-btn>
        </v-snackbar>

        <v-card class="box pa-4">
            <div class="text-h6 font-weight-bold text-center mb-4">Link Subjects</div>

            <!-- Top Header Summary Table -->
            <div class="mb-4">
                <table class="simple-exam-table">
                    <thead>
                        <tr>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Exam Conduct Type</th>
                            <th>Stream</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ schedule_data.exam_name || '-' }}</td>
                            <td>{{ schedule_data.academic_year || '-' }}</td>
                            <td>{{ schedule_data.semester || '-' }}</td>
                            <td>{{ schedule_data.start_date || '-' }}</td>
                            <td>{{ schedule_data.end_date || '-' }}</td>
                            <td>{{ formatList(schedule_data.exam_conduct_types) }}</td>
                            <td>{{ formatList(schedule_data.streams) }}</td>
                        </tr>
                    </tbody>
                    <thead>
                        <tr>
                            <th>Standard</th>
                            <th>Session No</th>
                            <th>Exam Date</th>
                            <th>Day</th>
                            <th>Slot No</th>
                            <th>From Time</th>
                            <th>To Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ formatStandards(schedule_data.standards) }}</td>
                            <td>{{ schedule_data.session_number || '-' }}</td>
                            <td>{{ formatDate(schedule_data.exam_date) }}</td>
                            <td>{{ schedule_data.day || '-' }}</td>
                            <td>{{ schedule_data.slot_no || '-' }}</td>
                            <td>{{ schedule_data.from_time || '-' }}</td>
                            <td>{{ schedule_data.to_time || '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Table Actions: Export, Search, Add Subject Icon -->
            <v-row dense align="center" class="mb-2">
                <v-col cols="12" sm="4" class="d-flex align-center">
                    <v-btn rounded color="success" outlined :loading="loader" @click="downloadFile">
                        <v-icon left color="success">mdi-export</v-icon>
                        Export
                    </v-btn>

                    <!-- Trigger Link Subject Dialog -->
                    <v-btn icon color="primary" class="ml-2" @click="openAddModal">
                        <v-icon medium>mdi-plus-circle</v-icon>
                    </v-btn>
                </v-col>

                <v-spacer></v-spacer>

                <v-col sm="6" md="4" cols="12">
                    <v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details dense outlined />
                </v-col>
            </v-row>

            <!-- Linked Subjects Data Table -->
            <v-data-table :headers="headers" :items="linked_subjects" class="elevation-1 mytable" dense :search="search">
                <template v-slot:item.sno="{ item }">
                    {{ linked_subjects.indexOf(item) + 1 }}
                </template>

                <template v-slot:item.action="{ item }">
                    <div class="d-flex justify-center align-center">
                        <v-btn icon small color="primary" class="mr-1" @click="editItem(item)">
                            <v-icon small>mdi-square-edit-outline</v-icon>
                        </v-btn>
                        <v-btn icon small color="error" class="mr-2" @click="confirmDelete(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <!-- Link / Edit Subject Modal Dialog -->
        <v-dialog v-model="linkSubjectDialog" max-width="400px" persistent>
            <v-card class="pa-4">
                <v-card-title class="headline justify-center font-weight-bold grey--text text--dark-2 pa-0 mb-4">
                    {{ editedId ? 'Edit Linked Subject' : 'Link Subject' }}
                </v-card-title>
                <v-card-text class="pa-0">
                    <v-form ref="linkSubjectForm" v-model="validForm">
                        <div class="mb-1 font-weight-bold">Exam Conduct Type <span class="red--text">*</span></div>
                        <v-select
                            v-model="newSubject.exam_conduct_type_id"
                            :items="master_conduct_types"
                            item-text="name"
                            item-value="id"
                            placeholder="Select Conduct Type"
                            outlined dense hide-details class="mb-3"
                        ></v-select>

                        <div class="mb-1 font-weight-bold">Stream <span class="red--text">*</span></div>
                        <v-select
                            v-model="newSubject.stream_id"
                            :items="master_streams"
                            item-text="name"
                            item-value="id"
                            placeholder="Select Stream"
                            outlined dense hide-details class="mb-3"
                        ></v-select>

                        <div class="mb-1 font-weight-bold">Standard <span class="red--text">*</span></div>
                        <v-select
                            v-model="newSubject.standard_id"
                            :items="master_standards"
                            item-text="standard"
                            item-value="id"
                            placeholder="Select Standard"
                            outlined dense hide-details class="mb-3"
                        ></v-select>

                        <div class="mb-1 font-weight-bold">Subject <span class="red--text">*</span></div>
                        <v-select
                            v-model="newSubject.course_offering_id"
                            :items="master_subjects"
                            item-text="display_name"
                            item-value="id"
                            placeholder="Select Subject"
                            outlined
                            dense
                            hide-details
                            class="mb-4"
                        ></v-select>
                </v-form>
                </v-card-text>
                <v-card-actions class="justify-center pa-0">
                    <v-btn color="primary" small
                        @click="editedId ? updateCoursewiseSchedule() : saveCoursewiseSchedule()">
                        Save
                    </v-btn>
                    <v-btn color="grey lighten-1" text small @click="closeModal">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-overlay :value="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";

export default {
    data: () => ({
        detailed_schedule_id: null,
        overlay: false,
        loader: false,
        snackbar: false,
        snackbar_msg: "",
        color: "",
        search: "",
        validForm: false,

        linkSubjectDialog: false,
        editedId: null,
        selectedDeleteItem: null,

        headers: [
            { text: "S.No", align: "center", value: "sno", sortable: false },
            { text: "Subject Code", align: "center", value: "course_code" },
            { text: "Subject Name", align: "center", value: "course_name" },
            { text: "Standard", align: "center", value: "standard" },
            { text: "Stream", align: "center", value: "stream" },
            { text: "Exam Conduct Type", align: "center", value: "exam_conduct_type" },
            { text: "Action", align: "center", value: "action", sortable: false }
        ],

        schedule_data: {},
        linked_subjects: [],

        master_conduct_types: [],
        master_streams: [],
        master_standards: [],
        master_subjects: [],

        newSubject: {
            exam_conduct_type_id: null,
            stream_id: null,
            standard_id: null,
            course_offering_id: null
        }
    }),

   methods: {
    formatDate(dateStr) {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    },

    formatList(arr) {
        if (!arr || !arr.length) return "-";
        return arr.map(item => typeof item === 'object' ? item.name : item).filter(Boolean).join(", ");
    },

    formatStandards(arr) {
        if (!arr || !arr.length) return "-";
        return arr.map(item => typeof item === 'object' ? item.standard : item).filter(Boolean).join(", ");
    },

    showSnackbar(clr, msg) {
        this.snackbar = true;
        this.color = clr;
        this.snackbar_msg = msg;
    },

    openAddModal() {
        this.editedId = null;
        this.resetForm();
        this.linkSubjectDialog = true;
    },

    editItem(item) {
        this.editedId = item.id;
        this.newSubject = {
            exam_conduct_type_id: item.exam_conduct_type_id || null,
            stream_id: item.stream_id || null,
            standard_id: item.standard_id || null,
            course_offering_id: item.course_offering_id || null
        };
        this.linkSubjectDialog = true;
    },

    closeModal() {
        this.linkSubjectDialog = false;
        this.editedId = null;
        this.resetForm();
    },

    resetForm() {
        this.newSubject = {
            exam_conduct_type_id: null,
            stream_id: null,
            standard_id: null,
            course_offering_id: null
        };
    },

    validateForm() {
        if (
            !this.newSubject.exam_conduct_type_id ||
            !this.newSubject.stream_id ||
            !this.newSubject.standard_id ||
            !this.newSubject.course_offering_id
        ) {
            this.showSnackbar("#b71c1c", "Please fill all required fields");
            return false;
        }
        return true;
    },

    // --- SAVE / LINK NEW SUBJECT FUNCTION ---
    saveCoursewiseSchedule() {
        if (!this.validateForm()) return;

        this.overlay = true;
        const payload = {
            detailed_schedule_id: this.detailed_schedule_id,
            exam_conduct_type_id: this.newSubject.exam_conduct_type_id,
            stream_id: this.newSubject.stream_id,
            standard_id: this.newSubject.standard_id,
            course_offering_id: this.newSubject.course_offering_id
        };

        axios
            .post("/SeatingArrangement/saveCoursewiseSchedule", payload)
            .then((res) => {
                if (res.data.msg === "200") {
                    this.showSnackbar("#2e7d32", res.data.text || "Subject linked successfully");
                    this.closeModal();
                    this.onLoad();
                } else {
                    this.showSnackbar("#b71c1c", res.data.text || "Failed to save subject");
                }
            })
            .catch((error) => {
                console.error("Save Error:", error);
                this.showSnackbar("#b71c1c", "Error saving subject link");
            })
            .finally(() => {
                this.overlay = false;
            });
    },

   updateCoursewiseSchedule() {
            if (
                !this.newSubject.exam_conduct_type_id ||
                !this.newSubject.stream_id ||
                !this.newSubject.standard_id ||
                !this.newSubject.course_offering_id
            ) {
                this.showSnackbar("#b71c1c", "Please fill all required fields");
                return;
            }

            this.overlay = true;

            const payload = {
                id: Number(this.editedId),
                detailed_schedule_id: Number(this.detailed_schedule_id),
                exam_conduct_type_id: Number(this.newSubject.exam_conduct_type_id),
                stream_id: Number(this.newSubject.stream_id),
                standard_id: Number(this.newSubject.standard_id),
                course_offering_id: Number(this.newSubject.course_offering_id)
            };

            axios
                .post("/SeatingArrangement/editCoursewiseSchedule", payload)
                .then((res) => {
                    if (res.data.msg === "200") {
                        this.showSnackbar("#2e7d32", res.data.text || "Subject updated successfully");
                        this.closeModal();
                        this.onLoad();
                    } else {
                        this.showSnackbar("#b71c1c", res.data.text || "Failed to update subject");
                    }
                })
                .catch((error) => {
                    console.error("Update Error:", error);
                    this.showSnackbar("#b71c1c", "Error updating subject");
                })
                .finally(() => {
                    this.overlay = false;
                });
        },

    onLoad() {
        if (!this.detailed_schedule_id) {
            this.showSnackbar("#b71c1c", "Detailed Schedule ID not found");
            return;
        }

        this.overlay = true;
        axios
            .post("/SeatingArrangement/getSubjectScheduleDetails", {
                detailed_schedule_id: this.detailed_schedule_id
            })
            .then((res) => {
                if (res.data.msg === "200") {
                    this.schedule_data = res.data.schedule_data || {};
                    this.linked_subjects = res.data.linked_subjects || [];

                    this.master_conduct_types = res.data.master_conduct_types || [];
                    this.master_streams = res.data.master_streams || [];
                    this.master_standards = res.data.master_standards || [];
                    this.master_subjects = res.data.master_subjects || [];
                } else {
                    this.showSnackbar("#b71c1c", res.data.text || "Failed to fetch details");
                }
            })
            .catch((error) => {
                console.error(error);
                this.showSnackbar("#b71c1c", "Error fetching subject details");
            })
            .finally(() => {
                this.overlay = false;
            });
    },

    confirmDelete(item) {
        if(confirm("Are you sure want to delete this subject linking")){
            this.overlay = true;
            axios
                .post("/SeatingArrangement/deleteCoursewiseSchedule", {
                    id: item.id
                })
                .then((res) => {
                    if (res.data.msg === "200") {
                        this.showSnackbar("#2e7d32", res.data.text || "Subject unlinked successfully");
                        this.deleteConfirmDialog = false;
                        this.selectedDeleteItem = null;
                        this.onLoad();
                    } else {
                        this.showSnackbar("#b71c1c", res.data.text || "Failed to delete item");
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.showSnackbar("#b71c1c", "Error deleting subject schedule");
                })
                .finally(() => {
                    this.overlay = false;
            });
        }
    },

    downloadFile() {
        this.loader = true;
        const excelData = this.linked_subjects.map((item, index) => ({
            "S.No": index + 1,
            "Subject Code": item.course_code,
            "Subject Name": item.course_name,
            "Standard": item.standard,
            "Stream": item.stream,
            "Exam Conduct Type": item.exam_conduct_type
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Linked Subjects");
        XLSX.writeFile(workbook, "LinkedSubjects.xlsx");
        this.loader = false;
    }
},

    mounted() {
        this.detailed_schedule_id = localStorage.getItem("detailed_schedule_id");
        this.onLoad();
    }
};
</script>

<style scoped>
.simple-exam-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #ffffff;
    text-align: center;
    border: 1px solid #dcdcdc;
}

.simple-exam-table th {
    background-color: #f2f2f2;
    color: #333333;
    font-weight: 600;
    font-size: 13px;
    padding: 8px;
    border: 1px solid #dcdcdc;
}

.simple-exam-table td {
    padding: 8px;
    font-size: 13px;
    color: #444444;
    border: 1px solid #dcdcdc;
}

.mytable /deep/ thead th {
    background: #0f2027;
    background: linear-gradient(to right, #2c5364, #203a43, #0f2027);
    color: white !important;
    font-weight: bold !important;
    text-align: center;
}
</style>